/** @file Applies one plan command to the shared draft; its errors and warnings point into the plan. */
import { current, type Draft } from 'immer';
import { docIndex } from '@pose2d/format/doc/docIndex';
import { parseOrThrow } from '@pose2d/format/parseOrThrow';
import { throwError } from '@pose2d/format/throwError';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { CommandCall, Doc, Id } from '@pose2d/format/types';
import type { CommandContext, NestedCommand } from '../commandContext';
import { resolveRef } from '../_shared/resolveRef';

/**
 * Commands a plan may not hold: the project index (D1) checks their effect on the top-level batch only (name
 * uniqueness, id-shaped names, trash state), so inside a plan they would skip those checks (final review C10).
 */
const TOP_LEVEL_ONLY = new Set(['autorig.applyPlan', 'project.rename', 'project.trash', 'project.restore']);

/** A path of the nested command (relative to its input) as a path of the plan: `<at>.input.<path>`, or `<at>`. */
function under(at: string, path: string | undefined): string {
  return path === undefined ? at : `${at}.input.${path}`;
}

/**
 * Runs `def` the way applyCommand would: parse, index of the draft as it is now, same ids/clock/warnings, and a
 * `created` of its own (returned), so a command never reads an entity an earlier plan command created (m39).
 */
function run(draft: Draft<Doc>, def: NestedCommand, input: unknown, ctx: CommandContext): Record<string, Id> {
  const parsed = parseOrThrow(def.input, input);
  const index = docIndex(current(draft));
  const created: Record<string, Id> = {};
  def.apply(draft, parsed, { ...ctx, index, resolve: (kind, ref) => resolveRef(index, kind, ref), created });
  return created;
}

/**
 * Applies plan command `call` (at `at`, e.g. `plan.commands.3`) and returns what it created (name -> id). An unknown
 * name, a nested applyPlan or a project lifecycle command fails at `<at>.name`; a Pose2DError it throws and every
 * warning it adds get their paths under `<at>.input`.
 */
export function applyNested(draft: Draft<Doc>, call: CommandCall, ctx: CommandContext, at: string): Record<string, Id> {
  const def = ctx.lookup(call.name) ?? throwError('NOT_FOUND', { kind: 'command', ref: call.name }, { path: `${at}.name` });
  if (TOP_LEVEL_ONLY.has(def.name)) {
    throwError('VALIDATION', undefined, { path: `${at}.name`, hint: `a plan cannot contain ${def.name}; send it as its own apply_commands entry` });
  }
  const from = ctx.warnings.length;
  let created: Record<string, Id> = {};
  try {
    created = run(draft, def, call.input, ctx);
  } catch (e) {
    const err = toPose2DError(e);
    if (err === null) throw e;
    throwError(err.code, err.params, { path: under(at, err.path), ...(err.hint === undefined ? {} : { hint: err.hint }) });
  }
  ctx.warnings.forEach((w, i) => {
    if (i >= from) ctx.warnings[i] = { ...w, path: under(at, w.path) };
  });
  return created;
}
